// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartLineBreakpoints } from "@hardwoods/Overrides/Widgets/Cart/helper";
import Info from "@hardwoods/XDL/Icons/Info";
import Pen from "@hardwoods/XDL/Icons/Pen";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { xGap } from "@hardwoods/XDL/Utilities/SharedStyles";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface PageContainerSharedStyles {
    loadingOverlay?: LoadingOverlayProps;
    titleSection?: InjectableCss;
    mainSection?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
}

export const pageContainerSharedStyles: PageContainerSharedStyles = {
    loadingOverlay: {
        css: css`
            width: 100%;
            padding-bottom: 70px;
        `,
    },
    titleSection: {
        css: css`
            margin-bottom: 50px;
        `,
    },
    mainSection: {
        css: css`
            display: flex;
            flex-direction: row;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                flex-wrap: wrap;
            }
        `,
    },
    leftSection: {
        css: css`
            flex: 1 1 100%;
            margin-right: 40px;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                margin-right: 0;
                margin-bottom: 40px;
            }
        `,
    },
    rightSection: {
        css: css`
            width: 330px;
            flex-shrink: 0;
            ${xScreen.lte("xs")} {
                margin: 0 auto;
                width: 100%;
            }
        `,
    },
};

interface CheckoutSectionSharedStyles {
    hiddenSection?: InjectableCss;
    sectionsContainer?: InjectableCss;
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    sectionGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    labelText?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
}

export const checkoutSectionsSharedStyles: CheckoutSectionSharedStyles = {
    hiddenSection: {
        css: css`
            display: none;
        `,
    },
    sectionsContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            font-size: 16px;
            line-height: 19px;
            &&&& {
                ${xGap("30px 70px")}
            }
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex: 1 1 250px;
            width: auto;
        `,
        offsetCss: css`
            &&&& {
                ${xGap("32px")}
            }
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 10px;
            position: relative;
        `,
    },
    sectionGridItem: {
        width: 12,
        css: css`
            display: block;
            &&& {
                flex: 1 1 250px;
            }
        `,
    },
    headingText: {
        size: 18,
        lineHeight: "22px",
        weight: "bold",
        css: css`
            flex-basis: 160px;
            margin-bottom: 0;
        `,
    },
    labelText: {
        weight: "bold",
        css: css`
            display: block;
            margin-bottom: 8px;
        `,
    },
    editLink: {
        color: "secondary",
        typographyProps: { size: 13, lineHeight: "22px", weight: "normal" },
        icon: { iconProps: { src: Pen, size: 12 } },
        css: css`
            margin-left: 10px;
            position: absolute;
            right: 0px;
        `,
    },
};

export const errorTextStyles: TypographyPresentationProps = {
    color: "danger",
    weight: "bold",
    size: 12,
    css: css`
        margin-top: 5px;
        display: block;
    `,
};

export const whatIsThisLinkStyles: LinkPresentationProps = {
    color: "secondary",
    icon: { iconProps: { src: Info, size: 16 } },
};
